import { useEffect, useRef, useState } from "react";

let apiPromise = null;
function loadYouTubeAPI() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve(window.YT);
    };
  });
  return apiPromise;
}

export function useYouTubePlayer(containerId, videoId, startSeconds = 0, autoplay = false, size = null) {
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playerState, setPlayerState] = useState(-1);
  const sizeRef = useRef(size);
  sizeRef.current = size;

  useEffect(() => {
    let interval;
    let destroyed = false;
    let retried = false;
    setReady(false);

    const createPlayer = (YT) => {
      const initialWidth = sizeRef.current?.width || 960;
      const initialHeight = sizeRef.current?.height || 540;
      playerRef.current = new YT.Player(containerId, {
        width: initialWidth,
        height: initialHeight,
        videoId,
        playerVars: {
          start: Math.floor(startSeconds || 0),
          rel: 0,
          autoplay: autoplay ? 1 : 0,
          controls: 0,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
        },
        events: {
          onReady: () => {
            if (destroyed) return;
            setReady(true);
            try {
              setDuration(playerRef.current.getDuration());
              if (sizeRef.current?.width && sizeRef.current?.height) {
                playerRef.current.setSize(sizeRef.current.width, sizeRef.current.height);
              }
            } catch (err) {
              // ignore — transient player-state race
            }
            interval = setInterval(() => {
              try {
                if (playerRef.current && playerRef.current.getCurrentTime) {
                  setCurrentTime(playerRef.current.getCurrentTime());
                }
              } catch (err) {
                // ignore — player mid-destroy/recreate
              }
            }, 500);
          },
          onStateChange: (e) => {
            setPlayerState(e.data);
          },
          onError: (e) => {
            // Transient embed failures (e.g. temporary CDN/network hiccups) can usually be
            // recovered by recreating the player once; genuinely unavailable videos will fail again.
            if (destroyed || retried) return;
            retried = true;
            clearInterval(interval);
            if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy();
            // Null out the ref immediately so any in-flight playVideo/pauseVideo/setSize call
            // (from user clicks or the ResizeObserver effect) becomes a safe optional-chained
            // no-op instead of touching the now-destroyed player's internals and crashing.
            playerRef.current = null;
            setTimeout(() => {
              if (!destroyed) createPlayer(YT);
            }, 1200);
          },
        },
      });
    };

    loadYouTubeAPI().then((YT) => {
      if (destroyed) return;
      createPlayer(YT);
    });

    return () => {
      destroyed = true;
      clearInterval(interval);
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
      }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Keep the underlying iframe's actual pixel size in sync with the measured container size
  // (YT.Player renders its own iframe with a fixed default size otherwise, causing letterboxing).
  useEffect(() => {
    if (ready && size?.width && size?.height && playerRef.current?.setSize) {
      try {
        playerRef.current.setSize(size.width, size.height);
      } catch (err) {
        // ignore — player mid-destroy/recreate
      }
    }
  }, [ready, size?.width, size?.height]);

  // Wrap every call into the YT.Player instance: its internal implementation can throw
  // (e.g. "Cannot read properties of null (reading 'src')") if invoked in a brief window
  // around destroy()/recreate, and since that's outside React's render cycle no error
  // boundary can catch it — an uncaught throw there would otherwise crash the whole app.
  const safeCall = (fn) => {
    try {
      fn();
    } catch (err) {
      // swallow — player is mid-transition (destroyed/recreating), safe to ignore
    }
  };

  const seekTo = (seconds) => {
    safeCall(() => playerRef.current?.seekTo?.(seconds, true));
  };

  const playVideo = () => safeCall(() => playerRef.current?.playVideo?.());
  const pauseVideo = () => safeCall(() => playerRef.current?.pauseVideo?.());

  return { playerRef, ready, currentTime, duration, playerState, seekTo, playVideo, pauseVideo };
}

// YT.PlayerState constants: -1 unstarted, 0 ended, 1 playing, 2 paused, 3 buffering, 5 cued
export const YT_STATE = { UNSTARTED: -1, ENDED: 0, PLAYING: 1, PAUSED: 2, BUFFERING: 3, CUED: 5 };
